import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "@/lib/motion";
import { Minus, Plus, Trash2, ShoppingBag, Gift } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export default function MiniCart() {
    const { cart, open, setOpen, subtotal, count, updateItemQty, updateBoxQty, removeItem, removeBox, setGiftWrap } = useCart();
    const navigate = useNavigate();

    const goCheckout = () => {
        setOpen(false);
        navigate("/checkout");
    };

    const Row = ({ image, name, sub, custom, price, qty, onDec, onInc, onRemove, testid }) => (
        <motion.div
            layout
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex gap-3 rounded-2xl bg-cream-100 p-3"
            data-testid={testid}
        >
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-cream-300">
                {image ? <img src={image} alt={name} className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-caramel/50"><Gift size={26} /></div>}
            </div>
            <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-roast">{name}</p>
                {sub && <p className="truncate text-xs text-mocha">{sub}</p>}
                {custom && <p className="text-[11px] leading-snug text-caramel/90">{custom}</p>}
                <p className="mt-1 text-sm font-semibold text-caramel">${price.toFixed(2)}</p>
                <div className="mt-2 flex items-center gap-2">
                    <button onClick={onDec} className="grid h-7 w-7 place-items-center rounded-full border border-caramel/20 text-roast hover:bg-cream-200" aria-label="Decrease"><Minus size={14} /></button>
                    <span className="w-6 text-center text-sm font-medium">{qty}</span>
                    <button onClick={onInc} className="grid h-7 w-7 place-items-center rounded-full border border-caramel/20 text-roast hover:bg-cream-200" aria-label="Increase"><Plus size={14} /></button>
                    <button onClick={onRemove} className="ml-auto grid h-7 w-7 place-items-center rounded-full text-mocha hover:text-destructive hover:bg-cream-200" aria-label="Remove"><Trash2 size={15} /></button>
                </div>
            </div>
        </motion.div>
    );

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent side="right" className="flex w-full flex-col bg-cream-50 border-caramel/10 sm:max-w-md p-0">
                <SheetHeader className="border-b border-caramel/10 px-6 py-5 text-left">
                    <SheetTitle className="font-serif text-2xl text-roast">Your Cart ({count})</SheetTitle>
                    <SheetDescription className="sr-only">Review the items in your cart and proceed to checkout.</SheetDescription>
                </SheetHeader>

                {count === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="empty-cart">
                        <div className="grid h-24 w-24 place-items-center rounded-full bg-banana/20 text-caramel">
                            <ShoppingBag size={40} strokeWidth={1.4} />
                        </div>
                        <p className="mt-5 font-serif text-xl text-roast">Your cart is feeling empty</p>
                        <p className="mt-1 text-sm text-mocha">Add a little sweetness to get started.</p>
                        <Button onClick={() => { setOpen(false); navigate("/shop"); }} className="mt-6 rounded-full bg-banana px-6 text-roast hover:bg-banana-hover" data-testid="empty-cart-shop-btn">
                            Shop Flavors
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
                            <AnimatePresence initial={false}>
                                {cart.items.map((i, idx) => (
                                    <Row
                                        key={`i-${i.product_id}-${i.size_key}-${idx}`}
                                        image={i.image}
                                        name={i.name}
                                        sub={i.size_label}
                                        custom={i.customization ? ([i.customization.base, i.customization.topping, i.customization.drizzle, i.customization.fruit].filter((v) => v && v !== "No thanks").join(" · ") || null) : null}
                                        price={i.unit_price * i.quantity}
                                        qty={i.quantity}
                                        onDec={() => updateItemQty(idx, i.quantity - 1)}
                                        onInc={() => updateItemQty(idx, i.quantity + 1)}
                                        onRemove={() => removeItem(idx)}
                                        testid={`cart-item-${idx}`}
                                    />
                                ))}
                                {cart.boxes.map((b, idx) => (
                                    <Row
                                        key={`b-${idx}`}
                                        image={null}
                                        name={b.label}
                                        sub={b.size_label}
                                        custom={b.flavors?.join(", ")}
                                        price={b.price * b.quantity}
                                        qty={b.quantity}
                                        onDec={() => updateBoxQty(idx, b.quantity - 1)}
                                        onInc={() => updateBoxQty(idx, b.quantity + 1)}
                                        onRemove={() => removeBox(idx)}
                                        testid={`cart-box-${idx}`}
                                    />
                                ))}
                            </AnimatePresence>

                            <label className="mt-2 flex items-center justify-between rounded-2xl border border-caramel/15 bg-cream-100 px-4 py-3">
                                <span className="flex items-center gap-2 text-sm font-medium text-roast">
                                    <Gift size={18} className="text-caramel" /> Add gift packaging <span className="text-mocha">+$6</span>
                                </span>
                                <Switch checked={cart.giftWrap} onCheckedChange={setGiftWrap} data-testid="gift-wrap-toggle" />
                            </label>
                        </div>

                        <div className="border-t border-caramel/10 px-6 py-5">
                            <div className="flex items-center justify-between text-base">
                                <span className="text-mocha">Subtotal</span>
                                <span data-testid="cart-subtotal" className="font-serif text-2xl text-roast">${subtotal.toFixed(2)}</span>
                            </div>
                            <p className="mt-1 text-xs text-mocha">Taxes & delivery calculated at checkout.</p>
                            <Button onClick={goCheckout} data-testid="checkout-btn" className="mt-4 h-12 w-full rounded-full bg-caramel text-base text-cream-50 hover:bg-caramel-dark">
                                Checkout
                            </Button>
                            <button onClick={() => { setOpen(false); navigate("/shop"); }} className="mt-3 w-full text-center text-sm font-medium text-caramel hover:underline">
                                Continue shopping
                            </button>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
